import { type PayloadAction, createSlice } from '@reduxjs/toolkit';
import type { AppDispatch, RootState } from '../index';

interface AdminState {
  isProcessing: boolean;
  error: string | null;
}

const initialState: AdminState = {
  isProcessing: false,
  error: null,
};

const adminSlice = createSlice({
  name: 'admin',
  initialState,
  reducers: {
    setProcessing: (state, action: PayloadAction<boolean>) => {
      state.isProcessing = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
  },
});

export const { setProcessing, setError } = adminSlice.actions;

// Admin action creators for vendor management
export const adminActions = {
  approveVendor: (vendorId: string) => async (dispatch: AppDispatch) => {
    dispatch(setProcessing(true));
    try {
      // TODO: Call API to approve vendor
      const response = await fetch(`http://localhost:3001/api/vendors/${vendorId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ hoaApprovalStatus: 'approved' }),
      });

      if (!response.ok) throw new Error('Failed to approve vendor');

      // Update vendor in store
      const { vendorsSlice } = await import('./vendorsSlice');
      const vendorData = await response.json();
      dispatch(vendorsSlice.actions.vendorUpdated({ id: vendorId, changes: vendorData }));
      dispatch(setProcessing(false));
    } catch (error) {
      dispatch(setError(error instanceof Error ? error.message : 'Failed to approve vendor'));
      dispatch(setProcessing(false));
      throw error;
    }
  },

  rejectVendor:
    ({ vendorId, reason }: { vendorId: string; reason: string }) =>
    async (dispatch: AppDispatch) => {
      dispatch(setProcessing(true));
      try {
        const response = await fetch(`http://localhost:3001/api/vendors/${vendorId}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ hoaApprovalStatus: 'rejected', rejectionReason: reason }),
        });

        if (!response.ok) throw new Error('Failed to reject vendor');

        const { vendorsSlice } = await import('./vendorsSlice');
        const vendorData = await response.json();
        dispatch(vendorsSlice.actions.vendorUpdated({ id: vendorId, changes: vendorData }));
        dispatch(setProcessing(false));
      } catch (error) {
        dispatch(setError(error instanceof Error ? error.message : 'Failed to reject vendor'));
        dispatch(setProcessing(false));
        throw error;
      }
    },

  setPreferredVendor: (vendorId: string) => async (dispatch: AppDispatch) => {
    dispatch(setProcessing(true));
    try {
      const response = await fetch(`http://localhost:3001/api/vendors/${vendorId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ contractType: 'preferred' }),
      });

      if (!response.ok) throw new Error('Failed to set preferred vendor');

      const { vendorsSlice } = await import('./vendorsSlice');
      const vendorData = await response.json();
      dispatch(vendorsSlice.actions.vendorUpdated({ id: vendorId, changes: vendorData }));
      dispatch(setProcessing(false));
    } catch (error) {
      dispatch(setError(error instanceof Error ? error.message : 'Failed to set preferred vendor'));
      dispatch(setProcessing(false));
      throw error;
    }
  },
};

export const selectAdminProcessing = (state: RootState) => state.admin?.isProcessing || false;
export const selectAdminError = (state: RootState) => state.admin?.error || null;

export default adminSlice.reducer;
